import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  Column,
  columnTitle,
  DetailsSortVariant,
  detailsViewByColumns,
  ViewBy,
} from '../../../../types/snapshot.types';
import {
  capTableExpandColClass,
  capTableRootClass,
  capTableStickyExpandClass,
  capTableStickyHeaderClass,
  capTableStickyNameClass,
  getCapTableViewByMode,
  hasCapTableExpandColumn,
} from './cap-table-sticky-classes';

export type TableProps = {
  additionalColumns?: PartialKeys<Column, string[]>;
  children: ReactNode;
  sortBy: DetailsSortVariant;
  viewBy: Record<ViewBy, boolean>;
  visibleColumns: Column[];
};

export const BasicTable: FC<TableProps> = ({
  children,
  sortBy,
  viewBy,
  visibleColumns,
  additionalColumns,
}) => {
  const selectedViewBy = getCapTableViewByMode(viewBy);
  const hasExpandColumn = hasCapTableExpandColumn(viewBy);
  const firstVisibleColumn = detailsViewByColumns[selectedViewBy][sortBy].find((column) =>
    visibleColumns.includes(column),
  );

  const headerCellClassName = (isFrozenName: boolean) =>
    twMerge(
      'px-4 pr-6 text-left',
      isFrozenName
        ? capTableStickyNameClass({ hasExpandColumn, isHeader: true })
        : capTableStickyHeaderClass,
    );

  return (
    <table className={capTableRootClass}>
      {hasExpandColumn && (
        <colgroup>
          <col className={capTableExpandColClass} />
        </colgroup>
      )}
      <thead className="bg-gray-50">
        <tr>
          {hasExpandColumn && (
            <th className={twMerge('text-left', capTableStickyExpandClass({ isHeader: true }))}>
              <span className="text-label-md font-medium text-[#172335]" />
            </th>
          )}
          {detailsViewByColumns[selectedViewBy][sortBy].map(
            (fieldNameKey) =>
              visibleColumns.includes(fieldNameKey) &&
              (additionalColumns?.[fieldNameKey] ? (
                additionalColumns?.[fieldNameKey].map((fieldName, additionalIndex) => (
                  <th
                    className={headerCellClassName(
                      fieldNameKey === firstVisibleColumn && additionalIndex === 0,
                    )}
                    key={fieldName}
                  >
                    <span className="text-label-md font-medium text-[#172335]">{fieldName}</span>
                  </th>
                ))
              ) : (
                <th
                  className={headerCellClassName(fieldNameKey === firstVisibleColumn)}
                  key={fieldNameKey}
                >
                  <span className="text-label-md font-medium text-[#172335]">
                    <AppFormattedMessage id={columnTitle[fieldNameKey]} />
                  </span>
                </th>
              )),
          )}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
};
